import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CatalogActionEntry } from "@layer/dashboard/types";
import {
  api,
  ApiError,
  type OauthClientRow,
  type OauthClientToolBox,
  type OauthClientsViewer,
} from "../lib/api";
import {
  deriveAgentScopeUniverse,
  groupScopesForClient,
  scopeSetsEqual,
  type ScopeFamilyGroup,
} from "../lib/oauth-scopes";
import { useCachedResource } from "../lib/resource-cache";
import {
  CONNECT_CODE_BUTTON_LABEL,
  CONNECT_CODE_BUTTON_TITLE,
  ConnectCodeReveal,
} from "../components/ConnectCodeReveal";

/**
 * MCP page — operator-facing read-only view of Clavis's third
 * canonical-action surface.
 *
 * Phase 28a shipped this page reading from a static
 * `agent-descriptions.ts` SoT module. Phase 28b lifted those strings
 * into per-action `agentDescription` registry entries (validation
 * enforces presence for agent-callable actions) and DELETED the SoT
 * module. This page now reads from `/api/catalog` — every
 * agent-callable action surfaces with its `agent_description` field.
 *
 * The grouping (read-lookup / read-list / mutation-single /
 * mutation-saga / outbound / gdpr) is derived per-call from each
 * action's `compensation.kind` + side-effects + name prefix; no
 * separate category column on the registry.
 */
export function Mcp(): JSX.Element {
  // Phase 67 — cache-backed; shares the `catalog` key with the Catalog
  // page so the `/api/catalog` fetch is reused across both.
  const { data: catalog, error } = useCachedResource("catalog", () =>
    api.catalog(),
  );

  if (error !== null) {
    return (
      <div>
        <h1 className="page-title">MCP</h1>
        <p className="hint">Failed to load /api/catalog: {error.message}</p>
      </div>
    );
  }
  if (catalog === undefined) {
    return (
      <div>
        <h1 className="page-title">MCP</h1>
        <p className="hint">Loading…</p>
      </div>
    );
  }

  // Filter to agent-callable + with a description set. Group by
  // derived category so the page stays readable.
  const agentActions = catalog.actions.filter(
    (a) =>
      a.allowed_actors.includes("agent") &&
      typeof a.agent_description === "string" &&
      a.agent_description.length > 0,
  );
  const grouped = groupByDerivedCategory(agentActions);

  return (
    <div>
      <h1 className="page-title">MCP</h1>
      <p className="hint">
        Clavis's third canonical-action surface — the Model Context Protocol
        server at <code>api.hercie.ai/mcp</code> (local dev: <code>localhost:3033</code>).
        REST is the first surface (the dashboard's <code>/api/dispatch/:action</code>);
        the CLI is the second; MCP is the third. AI agents (Claude, etc.)
        authenticate via OAuth 2.1 client_credentials grant + call canonical
        actions exposed as MCP tools, routed through the same dispatch
        chokepoint as every other surface.
      </p>

      {/* First, because managing the clients is what this page is opened for;
          the descriptions below are reference text. */}
      <OauthClientsPanel actions={catalog.actions} />

      <div className="panel">
        <h2>
          Tool descriptions · {agentActions.length} agent-callable canonical actions
        </h2>
        <p>
          These are the <code>agentDescription</code> registry strings the{" "}
          MCP server returns in its <code>tools/list</code> response under
          the <code>description</code> field. Phase 28b lifted these
          strings from the deleted{" "}
          <code>apps/dashboard/web/src/data/agent-descriptions.ts</code>{" "}
          SoT module into per-action registry entries. Human-readable
          mirror at{" "}
          <code>build-output/architecture/agent-descriptions.md</code>.
        </p>
        {(Object.keys(CATEGORY_LABELS) as DerivedCategory[]).map((cat) => {
          const entries = grouped.get(cat) ?? [];
          if (entries.length === 0) return null;
          const meta = CATEGORY_LABELS[cat];
          // Collapsed by default (no `open`), as the CLI page does its verbs:
          // the summary still names the group and counts it.
          return (
            <details className="agent-description-group collapsible" key={cat}>
              <summary>
                {meta.label} · {entries.length}
              </summary>
              <p className="hint">{meta.description}</p>
              <table className="agent-description-table table-cards">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.name}>
                      <td className="table-card-title">
                        <code>{e.name}</code>
                      </td>
                      <td data-label="Description" className="table-card-prose">
                        {e.agent_description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          );
        })}
      </div>

      <div className="panel">
        <h2>Recent token mints</h2>
        <p>
          Every <code>POST /oauth/token</code> success or failure writes one{" "}
          <code>audit_log</code> row attributing to{" "}
          <code>system:oauth:&lt;client_id&gt;</code> (a new operational
          actor prefix on the existing <code>system:*</code> family — NOT a
          new actor kind). Filter the audit log directly at{" "}
          <Link to="/data/audit_log">/data/audit_log</Link>.
        </p>
        <p>
          Tool-call attributions appear as{" "}
          <code>agent:&lt;client_id&gt;</code> on the same{" "}
          <code>audit_log</code> table (phase 28b chokepoint round-trip).
        </p>
      </div>
    </div>
  );
}

/**
 * The category vocabulary is OWNED BY THE SERVER — `catalog/action-category.ts`
 * derives it from each action's `compensation` + `sideEffects` and ships it on
 * the catalog entry. Aliasing the wire type here keeps `CATEGORY_LABELS` below
 * exhaustive: add a category server-side and this page stops compiling until it
 * gains a matching label, instead of rendering an unlabelled group.
 *
 * This replaced a local `deriveCategory()` that matched action names literally.
 * Anything registered after that list was written fell through to
 * `mutation-single`, so new sagas and new outbound vendor calls were shown
 * under the wrong heading from the day they shipped.
 */
type DerivedCategory = CatalogActionEntry["category"];

const CATEGORY_LABELS: Record<
  DerivedCategory,
  { label: string; description: string }
> = {
  "read-lookup": {
    label: "Read · single entity lookups",
    description:
      "Read one row by primary key or unique identifier. Replays return the same shape with PII fields nulled.",
  },
  "read-list": {
    label: "Read · paginated lists",
    description:
      "Cursor-paginated reads with optional filters. Default limit 50, max 200.",
  },
  "mutation-single": {
    label: "Mutation · single-step writes",
    description:
      "Synchronous mutations through the chokepoint. Return the canonical id / vendor reference directly.",
  },
  "mutation-saga": {
    label: "Mutation · multi-step sagas (async)",
    description:
      "Return HTTP 202 + run_id synchronously; saga runs async via Inngest. Poll lookup_saga_run for terminal state.",
  },
  outbound: {
    label: "Outbound · vendor APIs",
    description:
      "Hit a vendor's API (Stripe, Resend). Idempotency keys flow through to the vendor as defense-in-depth.",
  },
  gdpr: {
    label: "GDPR",
    description:
      "Subject Access Request + erasure. Staff or operator only at MVP; agent widening requires the reserved scope grants.",
  },
};


function groupByDerivedCategory(
  entries: ReadonlyArray<CatalogActionEntry>,
): Map<DerivedCategory, CatalogActionEntry[]> {
  const m = new Map<DerivedCategory, CatalogActionEntry[]>();
  for (const e of entries) {
    const cat = e.category;
    if (!m.has(cat)) m.set(cat, []);
    m.get(cat)!.push(e);
  }
  return m;
}

// ---------------------------------------------------------------------------
// OAuth clients panel (2026-08-24) — the live per-client permission editor
// over `/api/oauth-clients/:slug`. Replaced the read-only "provision via the
// CLI" panel. The scope universe is DERIVED from the catalog on every render
// (`deriveAgentScopeUniverse` — the union of `required_scopes.agent` across
// agent-callable actions), so a new action's scope appears here the moment it
// registers, and a scope that unlocks nothing is never offered. The CLI verbs
// (`pnpm cli oauth client …`) remain as the second surface over the same
// repository.
// ---------------------------------------------------------------------------

/**
 * The one-time reveal — either a freshly-minted client_secret (new
 * client) or a connect code (ChatGPT, Claude or another browser-OAuth
 * connector — `ConnectCodeReveal`). Both are shown ONCE and only the
 * hash is stored, so the reveal is unmountable ONLY by its own dismiss.
 */
type Reveal =
  | {
      readonly kind: "secret";
      readonly client_id: string;
      readonly name: string;
      readonly value: string;
    }
  | {
      readonly kind: "connect_code";
      readonly client_id: string;
      readonly name: string;
      readonly value: string;
      readonly expires_at: string;
      /** Set when the grant acts as the viewer (a person-bound connector). */
      readonly acts_as?: {
        readonly name: string;
        readonly tier: "staff" | "admin";
      };
    };

/**
 * Unsaved tool-box changes on one person-bound connector: the switched-off
 * list being edited, and the list it started from. A save sends both, and
 * lands only if the stored list is still `base`.
 */
interface ToolDraft {
  readonly base: ReadonlyArray<string>;
  readonly off: ReadonlyArray<string>;
}

function ScopeGrid(props: {
  readonly groups: ReadonlyArray<ScopeFamilyGroup>;
  readonly selected: ReadonlyArray<string>;
  readonly disabled: boolean;
  readonly idPrefix: string;
  readonly onToggle: (scope: string, checked: boolean) => void;
}): JSX.Element {
  const selectedSet = new Set(props.selected);
  return (
    <div className="oauth-scope-grid">
      {props.groups.map((g) => (
        <div className="oauth-scope-family" key={g.family}>
          <div className="oauth-scope-family-label">{g.family}</div>
          {g.scopes.map((s) => (
            <label
              className="oauth-scope-row"
              key={s.scope}
              title={
                s.unknown
                  ? "Legacy scope — no registered action requires it for agents. Untick to drop it on the next save."
                  : `Unlocks: ${s.unlocks.join(", ")}`
              }
            >
              <input
                type="checkbox"
                id={`${props.idPrefix}-${s.scope}`}
                checked={selectedSet.has(s.scope)}
                disabled={props.disabled}
                onChange={(e) => props.onToggle(s.scope, e.target.checked)}
              />
              <code>{s.scope}</code>
              <span className="hint">
                {s.unknown
                  ? "legacy"
                  : `${s.unlocks.length} tool${s.unlocks.length === 1 ? "" : "s"}`}
              </span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * A person-bound connector's tool boxes (`mcp-connector-tool-boxes`,
 * 2026-10-14): one box per tool its tier could ever list, grouped as the
 * descriptions below are. A tool is on for the connector only when Config
 * gives it to the tier AND its box is ticked, so a box Config has off is drawn
 * dimmed but keeps its own tick, which decides again the day Config switches
 * the tool on. The boxes come from the server, read off the resolution the MCP
 * server enforces with; nothing here re-derives a tier rule.
 */
function ToolBoxes(props: {
  readonly boxes: ReadonlyArray<OauthClientToolBox>;
  readonly categoryOf: ReadonlyMap<string, DerivedCategory>;
  /** The switched-off list being edited (the saved one until a box changes). */
  readonly off: ReadonlySet<string>;
  readonly editable: boolean;
  readonly idPrefix: string;
  readonly onSet: (names: ReadonlyArray<string>, on: boolean) => void;
}): JSX.Element {
  // A tool the catalog has no entry for still gets its box, under "Other",
  // rather than vanishing from the panel while staying switchable by name.
  const byCategory = new Map<DerivedCategory | "other", OauthClientToolBox[]>();
  for (const box of props.boxes) {
    const cat = props.categoryOf.get(box.name) ?? "other";
    const list = byCategory.get(cat) ?? [];
    list.push(box);
    byCategory.set(cat, list);
  }
  const order: ReadonlyArray<DerivedCategory | "other"> = [
    ...(Object.keys(CATEGORY_LABELS) as DerivedCategory[]),
    "other",
  ];
  return (
    <div className="oauth-tool-groups">
      {order.map((cat) => {
        const boxes = byCategory.get(cat) ?? [];
        if (boxes.length === 0) return null;
        const live = boxes.filter(
          (b) => b.on_config && !props.off.has(b.name),
        ).length;
        const offInConfig = boxes.filter((b) => !b.on_config).length;
        const names = boxes.map((b) => b.name);
        return (
          <details className="oauth-tool-group collapsible" key={cat}>
            <summary>
              {cat === "other" ? "Other" : CATEGORY_LABELS[cat].label} ·{" "}
              {live} of {boxes.length} on
              {offInConfig > 0 ? ` · ${offInConfig} off in Config` : ""}
            </summary>
            {props.editable && (
              <div className="oauth-tool-group-actions">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => props.onSet(names, true)}
                >
                  All on
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => props.onSet(names, false)}
                >
                  All off
                </button>
              </div>
            )}
            <div className="oauth-tool-grid">
              {boxes.map((b) => (
                <label
                  className={
                    b.on_config
                      ? "oauth-scope-row"
                      : "oauth-scope-row oauth-tool-off-in-config"
                  }
                  key={b.name}
                  title={
                    b.on_config
                      ? undefined
                      : "Config does not give this tool to this tier, so it stays off whatever its box says. The box decides again if Config switches it on."
                  }
                >
                  <input
                    type="checkbox"
                    id={`${props.idPrefix}-tool-${b.name}`}
                    checked={!props.off.has(b.name)}
                    disabled={!props.editable}
                    onChange={(e) => props.onSet([b.name], e.target.checked)}
                  />
                  <span className="oauth-tool-name">
                    <code>{b.name}</code>
                    {!b.on_config && (
                      <>
                        {" "}
                        <span className="hint">off in Config</span>
                      </>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

function formatWhen(iso: string | null): string {
  if (iso === null) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function OauthClientsPanel(props: {
  readonly actions: ReadonlyArray<CatalogActionEntry>;
}): JSX.Element {
  const { data: who } = useCachedResource("whoami", () => api.whoami());
  const slug = who?.studio?.slug ?? null;

  const [clients, setClients] = useState<ReadonlyArray<OauthClientRow> | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  // Per-client draft scope lists, keyed by client_id. Absent = no edits.
  const [drafts, setDrafts] = useState<Record<string, ReadonlyArray<string>>>(
    {},
  );
  // Per-connector tool-box edits (a person-bound connector's switched-off
  // list), keyed by client_id. Absent = no edits.
  const [toolDrafts, setToolDrafts] = useState<Record<string, ToolDraft>>({});
  // client_id of the in-flight mutation, or "create". Disables that card.
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // The one-time reveal (client_secret OR connect code). Survives list
  // reloads by design — it CANNOT be re-fetched, so nothing may unmount it
  // except its own dismiss button.
  const [minted, setMinted] = useState<Reveal | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState<ReadonlyArray<string>>([]);
  // The signed-in person as a connector would act as them (null = this login
  // has no staff row, so "acts as me" is not offered).
  const [viewer, setViewer] = useState<OauthClientsViewer | null>(null);
  // New-client form: a scoped agent grant, or a connector that acts as me.
  const [newKind, setNewKind] = useState<"agent" | "me">("agent");
  const [newCapAtStaff, setNewCapAtStaff] = useState(false);

  const universe = useMemo(
    () => deriveAgentScopeUniverse(props.actions),
    [props.actions],
  );
  // Each tool box's group, from the catalog entries the descriptions below
  // are grouped by.
  const categoryOf = useMemo(
    () => new Map(props.actions.map((a) => [a.name, a.category] as const)),
    [props.actions],
  );

  useEffect(() => {
    if (slug === null) return;
    let cancelled = false;
    api
      .getOauthClients(slug)
      .then((r) => {
        if (!cancelled) {
          setClients(r.clients);
          setViewer(r.viewer);
          setLoadError(null);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const reload = async (): Promise<void> => {
    if (slug === null) return;
    const r = await api.getOauthClients(slug);
    setClients(r.clients);
    setViewer(r.viewer);
  };

  const toggleDraft = (
    client: OauthClientRow,
    scope: string,
    checked: boolean,
  ): void => {
    const current = drafts[client.client_id] ?? client.granted_scopes;
    const next = checked
      ? [...new Set([...current, scope])]
      : current.filter((s) => s !== scope);
    setDrafts({ ...drafts, [client.client_id]: next });
  };

  const saveScopes = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    const draft = drafts[client.client_id];
    if (draft === undefined) return;
    setBusy(client.client_id);
    setActionError(null);
    try {
      await api.updateOauthClientScopes(slug, client.client_id, draft);
      const rest = { ...drafts };
      delete rest[client.client_id];
      setDrafts(rest);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Switch tools on (`on`) or off in a connector's draft. Names outside the
  // boxes shown — switched off at a tier the connector no longer has — stay
  // as they are, so a save keeps them. The first change records the list it
  // started from, which the save sends back.
  const setToolsDraft = (
    client: OauthClientRow,
    names: ReadonlyArray<string>,
    on: boolean,
  ): void => {
    setToolDrafts((prev) => {
      const draft = prev[client.client_id] ?? {
        base: client.disabled_tools,
        off: client.disabled_tools,
      };
      const off = new Set(draft.off);
      for (const name of names) {
        if (on) off.delete(name);
        else off.add(name);
      }
      return {
        ...prev,
        [client.client_id]: { base: draft.base, off: [...off].sort() },
      };
    });
  };

  const saveTools = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    const draft = toolDrafts[client.client_id];
    if (draft === undefined) return;
    const dropDraft = (): void =>
      setToolDrafts((prev) => {
        const rest = { ...prev };
        delete rest[client.client_id];
        return rest;
      });
    setBusy(client.client_id);
    setActionError(null);
    try {
      await api.updateOauthClientTools(
        slug,
        client.client_id,
        draft.off,
        draft.base,
      );
      dropDraft();
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.code === "oauth_client_tools_changed") {
        // Someone saved this connector's tools after this page read them, so
        // nothing was written. Show what is saved now; the change is theirs
        // to make again, on top of it.
        dropDraft();
        let reloaded = true;
        try {
          await reload();
        } catch {
          reloaded = false;
        }
        setActionError(
          `${client.name}'s tools were changed elsewhere after this page loaded them, so nothing was saved. ${
            reloaded
              ? "Its boxes now show what is saved; make your change again."
              : "Reload the page, then make your change again."
          }`,
        );
      } else {
        setActionError((e as Error).message);
      }
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    const ok = window.confirm(
      `Revoke ${client.name} (${client.client_id})?\n\nEvery request it makes fails immediately — outstanding tokens die with it. This cannot be undone; create a new client to restore access.`,
    );
    if (!ok) return;
    setBusy(client.client_id);
    setActionError(null);
    try {
      await api.revokeOauthClient(slug, client.client_id);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const resetCreateForm = (): void => {
    setShowCreate(false);
    setNewName("");
    setNewScopes([]);
    setNewKind("agent");
    setNewCapAtStaff(false);
  };

  // A connector that acts as ME. There is no secret to reveal: it connects
  // with a connect code only, so one is minted straight away and shown.
  const createPersonal = async (): Promise<void> => {
    if (slug === null || viewer === null) return;
    setBusy("create");
    setActionError(null);
    try {
      const created = await api.createPersonalOauthClient(slug, {
        name: newName.trim(),
        tier_cap: newCapAtStaff ? "staff" : null,
      });
      resetCreateForm();
      await reload();
      const code = await api.issueOauthConnectCode(
        slug,
        created.client.client_id,
      );
      setMinted({
        kind: "connect_code",
        client_id: created.client.client_id,
        name: created.client.name,
        value: code.connect_code,
        expires_at: code.connect_code_expires_at,
        acts_as: {
          name: viewer.display_name,
          tier: created.client.tier_cap === "staff" ? "staff" : viewer.tier,
        },
      });
      setCopied(false);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const create = async (): Promise<void> => {
    if (slug === null) return;
    if (newKind === "me") {
      await createPersonal();
      return;
    }
    setBusy("create");
    setActionError(null);
    try {
      const r = await api.createOauthClient(slug, {
        name: newName.trim(),
        scopes: newScopes,
      });
      setMinted({
        kind: "secret",
        client_id: r.client.client_id,
        name: r.client.name,
        value: r.client_secret,
      });
      setCopied(false);
      resetCreateForm();
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const issueConnectCode = async (client: OauthClientRow): Promise<void> => {
    if (slug === null) return;
    setBusy(client.client_id);
    setActionError(null);
    try {
      const r = await api.issueOauthConnectCode(slug, client.client_id);
      setMinted({
        kind: "connect_code",
        client_id: client.client_id,
        name: client.name,
        value: r.connect_code,
        expires_at: r.connect_code_expires_at,
        // Only the person a connector acts as can mint its code, so a
        // person-bound reveal is always about the viewer.
        ...(client.acts_as === "staff" &&
        client.person?.display_name != null &&
        client.person.tier != null
          ? {
              acts_as: {
                name: client.person.display_name,
                tier: client.person.tier,
              },
            }
          : {}),
      });
      setCopied(false);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const copySecret = async (): Promise<void> => {
    if (minted === null) return;
    try {
      await navigator.clipboard.writeText(minted.value);
      setCopied(true);
    } catch {
      // Clipboard can be unavailable (non-HTTPS dev hosts) — the value is
      // on screen either way; selection + ⌘C still works.
    }
  };

  const active = (clients ?? []).filter((c) => c.revoked_at === null);
  const revoked = (clients ?? []).filter((c) => c.revoked_at !== null);

  return (
    <div className="panel">
      <h2>OAuth clients · who may call this, and with what</h2>
      <p className="hint">
        Each client is a long-lived credential for one MCP-speaking agent.
        Its ticked scopes decide which tools <code>tools/list</code> offers
        it — default-deny, reads and writes ticked separately. A scope
        change takes effect on the client&apos;s next token
        (≤1&nbsp;hour, or relaunch the app);{" "}
        <strong>revocation is immediate</strong>. To connect{" "}
        <strong>ChatGPT</strong> or <strong>Claude</strong> (web, Desktop or
        mobile), press <strong>{CONNECT_CODE_BUTTON_LABEL}</strong> on a client
        for a one-time code the person pastes on the Clavis approval page. The{" "}
        <code>client_secret</code> from <strong>New client…</strong> is for the
        local stdio bridge and other server-side callers. A client can instead{" "}
        <strong>act as you</strong>: it then has no scopes and no secret,
        follows your role (staff: the staff chat tool list, and nothing until
        the studio has written one; admin: every staff action bar a fixed
        few), and every call is logged under your name. Its tool boxes switch
        single tools off for that connector alone. Live table:{" "}
        <Link to="/data/oauth_clients">/data/oauth_clients</Link>; CLI twin:{" "}
        <code>pnpm cli oauth client …</code>.
      </p>
      {loadError !== null && (
        <p className="hint">Failed to load clients: {loadError}</p>
      )}
      {actionError !== null && (
        <p className="oauth-client-error">Request failed: {actionError}</p>
      )}
      {minted !== null && minted.kind === "secret" && (
        <div className="oauth-secret-reveal">
          <h3>
            Credentials for {minted.name} — the secret is shown ONCE
          </h3>
          <p className="hint">
            Only a hash is stored. Copy it now and put it straight into the
            client&apos;s config; if it is lost, revoke and create a new
            client.
          </p>
          <div className="oauth-secret-line">
            <span>client_id</span>
            <code>{minted.client_id}</code>
          </div>
          <div className="oauth-secret-line">
            <span>client_secret</span>
            <code>{minted.value}</code>
          </div>
          <div className="oauth-secret-actions">
            <button type="button" onClick={() => void copySecret()}>
              {copied ? "Copied ✓" : "Copy secret"}
            </button>
            <button
              type="button"
              onClick={() => {
                const ok = window.confirm(
                  "Dismiss the secret? It cannot be shown again.",
                );
                if (ok) setMinted(null);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {minted !== null && minted.kind === "connect_code" && (
        <ConnectCodeReveal
          name={minted.name}
          code={minted.value}
          expiresLabel={formatWhen(minted.expires_at)}
          {...(minted.acts_as !== undefined ? { actsAs: minted.acts_as } : {})}
          copied={copied}
          onCopy={() => void copySecret()}
          onDismiss={() => {
            const ok = window.confirm(
              "Dismiss the connect code? It cannot be shown again — issue a fresh one if needed.",
            );
            if (ok) setMinted(null);
          }}
        />
      )}
      {clients === null && loadError === null && (
        <p className="hint">Loading clients…</p>
      )}
      {clients !== null && active.length === 0 && (
        <p className="hint">No active clients.</p>
      )}
      {active.map((client) => {
        const draft = drafts[client.client_id] ?? client.granted_scopes;
        const dirty = !scopeSetsEqual(draft, client.granted_scopes);
        const groups = groupScopesForClient(universe, client.granted_scopes);
        const isBusy = busy === client.client_id;
        if (client.acts_as === "staff") {
          // A connector that acts as a person: no scopes to edit. What it may
          // call follows that person's chat tier, so the card says who and at
          // what tier, and offers the connect code to that person only.
          const person = client.person ?? null;
          // Three states, and only one of them is "this person left": a staff
          // list that could not be read says nothing about anyone.
          const unchecked = person === null || person.lookup === "unavailable";
          const gone = !unchecked && person.display_name === null;
          const isYou = person?.is_you === true;
          // Its person or any admin may switch its tools (Mario, 2026-10-14);
          // the server checks the same and refuses everyone else.
          const canEditTools = isYou || viewer?.tier === "admin";
          const tools = client.tools ?? null;
          const boxes =
            tools !== null && tools.lookup === "present" ? tools.boxes : null;
          const toolDraft = toolDrafts[client.client_id];
          const toolsOff = new Set(toolDraft?.off ?? client.disabled_tools);
          const toolsDirty =
            toolDraft !== undefined &&
            !scopeSetsEqual(toolDraft.off, toolDraft.base);
          const liveTools =
            boxes?.filter((b) => b.on_config && !toolsOff.has(b.name))
              .length ?? 0;
          return (
            <div className="oauth-client-card" key={client.client_id}>
              <div className="oauth-client-head">
                <div>
                  <strong>{client.name}</strong>{" "}
                  <code>{client.client_id}</code>
                </div>
                <div className="hint">
                  created {formatWhen(client.created_at)} · last used{" "}
                  {formatWhen(client.last_used_at)}
                </div>
              </div>
              {unchecked ? (
                <p className="oauth-client-error">
                  Could not read the staff list, so who this connector acts as
                  is not shown. Reload to check again. You can still revoke it.
                </p>
              ) : gone ? (
                <p className="oauth-client-error">
                  The person this connector acted as is no longer on the staff
                  list. It stops working on its next request.
                </p>
              ) : (
                <p>
                  Acts as{" "}
                  <strong>
                    {person.display_name}
                    {isYou ? " (you)" : ""}
                  </strong>{" "}
                  at the <strong>{person.tier}</strong> tier
                  {client.tier_cap === "staff"
                    ? " — capped there, whatever their role"
                    : ""}
                  .{" "}
                  <span className="hint">
                    {person.tier === "admin" ? (
                      <>
                        It can use every staff action, including sending
                        WhatsApp messages, emails and both newsletters, except
                        30-day payment links (a one-off checkout link still
                        works), secrets, studio settings and package expiry
                        dates. If <Link to="/config">Config</Link> sets an
                        admin chat tool list, it is held to that list.
                      </>
                    ) : (
                      <>
                        It can use the tools on the staff chat list (
                        <Link to="/config">Config</Link> → chat tools), except
                        30-day payment links. If the studio has no staff list,
                        it has no tools.
                      </>
                    )}{" "}
                    Every call is logged under their name.
                  </span>
                </p>
              )}
              {!unchecked && !gone && boxes !== null && (
                <div className="oauth-tool-boxes">
                  <div className="oauth-scope-family-label">
                    Tools · {liveTools} of {boxes.length} on
                  </div>
                  <p className="hint">
                    A tool is on only when <Link to="/config">Config</Link>{" "}
                    gives it to the {person.tier} tier and its box here is
                    ticked. Unticking takes it from this connector alone, on
                    its next request; ticking gives back only what Config
                    allows.
                    {canEditTools
                      ? ""
                      : ` Only ${person.display_name ?? "its person"} or an admin can change these.`}
                  </p>
                  <ToolBoxes
                    boxes={boxes}
                    categoryOf={categoryOf}
                    off={toolsOff}
                    editable={canEditTools && !isBusy}
                    idPrefix={client.client_id}
                    onSet={(names, on) => setToolsDraft(client, names, on)}
                  />
                </div>
              )}
              {!unchecked && !gone && tools?.lookup === "unavailable" && (
                <p className="oauth-client-error">
                  Could not read the studio&apos;s chat tool lists, so this
                  connector&apos;s tools are not shown. Reload to check again.
                </p>
              )}
              <div className="oauth-client-actions">
                {boxes !== null && canEditTools && !unchecked && !gone && (
                  <button
                    type="button"
                    disabled={!toolsDirty || isBusy}
                    onClick={() => void saveTools(client)}
                  >
                    {isBusy ? "Saving…" : "Save tools"}
                  </button>
                )}
                {toolsDirty && <span className="hint">Unsaved changes.</span>}
                <button
                  type="button"
                  disabled={isBusy || unchecked || gone || !isYou}
                  onClick={() => void issueConnectCode(client)}
                  title={
                    isYou
                      ? CONNECT_CODE_BUTTON_TITLE
                      : "Only the person a connector acts as can get its connect code."
                  }
                >
                  {isBusy ? "Working…" : CONNECT_CODE_BUTTON_LABEL}
                </button>
                <button
                  type="button"
                  className="oauth-revoke-btn"
                  disabled={isBusy}
                  onClick={() => void revoke(client)}
                >
                  Revoke
                </button>
              </div>
            </div>
          );
        }
        return (
          <div className="oauth-client-card" key={client.client_id}>
            <div className="oauth-client-head">
              <div>
                <strong>{client.name}</strong>{" "}
                <code>{client.client_id}</code>
              </div>
              <div className="hint">
                created {formatWhen(client.created_at)} · last used{" "}
                {formatWhen(client.last_used_at)} ·{" "}
                {client.granted_scopes.length} scope
                {client.granted_scopes.length === 1 ? "" : "s"}
              </div>
            </div>
            <ScopeGrid
              groups={groups}
              selected={draft}
              disabled={isBusy}
              idPrefix={client.client_id}
              onToggle={(scope, checked) =>
                toggleDraft(client, scope, checked)
              }
            />
            <div className="oauth-client-actions">
              <button
                type="button"
                disabled={!dirty || isBusy || draft.length === 0}
                onClick={() => void saveScopes(client)}
              >
                {isBusy ? "Saving…" : "Save scopes"}
              </button>
              {dirty && draft.length === 0 && (
                <span className="hint">
                  At least one scope is required — revoke instead to remove
                  access.
                </span>
              )}
              {dirty && draft.length > 0 && (
                <span className="hint">Unsaved changes.</span>
              )}
              <button
                type="button"
                disabled={isBusy}
                onClick={() => void issueConnectCode(client)}
                title={CONNECT_CODE_BUTTON_TITLE}
              >
                {isBusy ? "Working…" : CONNECT_CODE_BUTTON_LABEL}
              </button>
              <button
                type="button"
                className="oauth-revoke-btn"
                disabled={isBusy}
                onClick={() => void revoke(client)}
              >
                Revoke
              </button>
            </div>
          </div>
        );
      })}
      {showCreate ? (
        <div className="oauth-client-card oauth-client-card-new">
          <div className="oauth-client-head">
            <strong>New client</strong>
          </div>
          <label className="oauth-new-name">
            Name{" "}
            <input
              type="text"
              value={newName}
              maxLength={128}
              placeholder="e.g. Eladio — Claude Desktop"
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <div className="oauth-new-kind">
            <label>
              <input
                type="radio"
                name="oauth-new-kind"
                checked={newKind === "agent"}
                disabled={busy === "create"}
                onChange={() => setNewKind("agent")}
              />{" "}
              Scoped agent — it gets only the tools you tick below
            </label>
            <label
              title={
                viewer === null
                  ? "Your login is not linked to a staff row in this studio, so a connector cannot act as you."
                  : undefined
              }
            >
              <input
                type="radio"
                name="oauth-new-kind"
                checked={newKind === "me"}
                disabled={busy === "create" || viewer === null}
                onChange={() => setNewKind("me")}
              />{" "}
              Acts as me
              {viewer !== null
                ? ` — ${viewer.display_name}, ${viewer.tier} tier`
                : " — unavailable for this login"}
            </label>
          </div>
          {newKind === "me" && viewer !== null ? (
            <>
              <p className="hint">
                {viewer.tier === "admin" && !newCapAtStaff
                  ? "The connector can use every staff action under your name, including sending WhatsApp messages, emails and both newsletters."
                  : "The connector can use the tools on the staff chat list, under your name; if the studio has no staff list, it has no tools."}{" "}
                It follows your role from then on: if your admin access is
                removed, so is the connector&apos;s, and if you are taken off
                the staff list it is revoked. Only you can connect it. 30-day
                payment links, secrets, studio settings and package expiry
                dates stay out of reach.
              </p>
              {viewer.tier === "admin" && (
                <label className="oauth-scope-row">
                  <input
                    type="checkbox"
                    checked={newCapAtStaff}
                    disabled={busy === "create"}
                    onChange={(e) => setNewCapAtStaff(e.target.checked)}
                  />
                  Keep it at the staff tier (it never gets my admin tools)
                </label>
              )}
            </>
          ) : (
            <ScopeGrid
              groups={groupScopesForClient(universe, [])}
              selected={newScopes}
              disabled={busy === "create"}
              idPrefix="new-client"
              onToggle={(scope, checked) =>
                setNewScopes(
                  checked
                    ? [...new Set([...newScopes, scope])]
                    : newScopes.filter((s) => s !== scope),
                )
              }
            />
          )}
          <div className="oauth-client-actions">
            <button
              type="button"
              disabled={
                busy === "create" ||
                newName.trim().length === 0 ||
                (newKind === "agent" && newScopes.length === 0)
              }
              onClick={() => void create()}
            >
              {busy === "create"
                ? "Creating…"
                : newKind === "me"
                  ? "Create and get connect code"
                  : "Create client"}
            </button>
            <button
              type="button"
              disabled={busy === "create"}
              onClick={resetCreateForm}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setShowCreate(true)}>
          New client…
        </button>
      )}
      {revoked.length > 0 && (
        <details className="oauth-revoked-list">
          <summary>
            Revoked · {revoked.length} client
            {revoked.length === 1 ? "" : "s"}
          </summary>
          {revoked.map((client) => (
            <div className="hint" key={client.client_id}>
              <code>{client.client_id}</code> {client.name} — revoked{" "}
              {formatWhen(client.revoked_at)}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
